import Box from "@/common/components/Box";
import Button from "@/common/components/Button";
import { DateInput } from "@/common/components/DateInput/DateInput";
import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";
import ResumeItem from "@/common/components/ResumeItem";
import { ShowHeader } from "@/common/components/ShowHeader";
import TextField from "@/common/components/TextField";
import { UnitTextField } from "@/common/components/UnitTextField";
import { useGoBack } from "@/common/hooks/useGoBack";
import useModalMessagesSimple from "@/common/hooks/useModalMessagesSimple";
import { formatCurrency } from "@/common/utils/formatters";
import { FormTransformers } from "@/common/utils/formTransformers";
import { maintenanceForwardingsDirectiveEntryKlasses } from "@/features/maintenance/features/workflows/constants/forwardings/maintenanceForwardingsDirectiveEntryKlasses.constant";
import { updateExecutionEntryEstablishmentAcceptSchema, UpdateExecutionEntryEstablishmentAcceptValidationSchema } from "@/features/maintenance/features/workflows/features/executionEntry/schemas/updateExecutionEntryEstablishmentAccept.schema";
import { updateExecutionEntryErrorsTransformer } from "@/features/maintenance/features/workflows/features/executionEntry/utils/updateExecutionEntryErrorsTransformer";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { ChevronsRightIcon, ClockIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { Controller, FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { useCreateMaintenanceEstablishmentChoiceAuctionEntryExecutionProposal } from "../../hooks/useCreateMaintenanceEstablishmentChoiceAuctionEntryExecutionProposal";
import { useMaintenanceEstablishmentChoiceAuctionEntry } from "../../hooks/useMaintenanceEstablishmentChoiceAuctionEntry";
import { useMaintenanceEstablishmentChoiceAuctionEntryUpdatedSummary } from "../../hooks/useMaintenanceEstablishmentChoiceAuctionEntryUpdatedSummary";
import ExecutionEntryUpdateObjectiveAttributes from "./components/ObjectiveAttributes";

const CreateExecutionAuctionProposal = () => {
  const { auctionEntryId } = useParams();

  const queryClient = useQueryClient();

  const goBack = useGoBack();

  const [formDisabled, setFormDisabled] = useState(false);

  const {
    setMessages,
  } = useModalMessagesSimple({
    isError: true,
  });

  const {
    data: auctionSummary,
  } = useMaintenanceEstablishmentChoiceAuctionEntryUpdatedSummary({
    auctionEntryId,
  });

  const {
    data: auctionEntry,
    isPending: auctionEntryIsPending,
    dataUpdatedAt: auctionEntryUpdatedAt,
  } = useMaintenanceEstablishmentChoiceAuctionEntry({
    auctionEntryId,
  });

  const formMethods = useForm<UpdateExecutionEntryEstablishmentAcceptValidationSchema>({
    resolver: zodResolver(updateExecutionEntryEstablishmentAcceptSchema),
    disabled: formDisabled,
  });

  const {
    control,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = formMethods

  const {
    mutate: createProposal,
    isPending: createProposalIsPending,
  } = useCreateMaintenanceEstablishmentChoiceAuctionEntryExecutionProposal({
    auctionEntryId,
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['maintenanceEstablishmentChoiceAuctionEntryUpdatedSummary', auctionEntryId] });
        queryClient.invalidateQueries({ queryKey: ['maintenanceEstablishmentChoiceAuctionEntry', auctionEntryId] });

        goBack({
          fallback: `/maintenance-establishment-choice/auction-entries/${auctionEntryId}`,
          searchLastRouteFromFallback: true,
        })
      },
      onError: (error) => {
        FormTransformers.errorsTransformer({
          setError,
          setMessages,
          transformer: updateExecutionEntryErrorsTransformer,
        })(error)
      },
    },
  })

  const onSubmit: SubmitHandler<UpdateExecutionEntryEstablishmentAcceptValidationSchema> = (data) =>
    createProposal(data);

      useEffect(() => {
        if (auctionEntryIsPending) {
          return
        }

        const pieces = auctionEntry?.forwarding?.directive_entry?.objectives?.flatMap(
          (objective) =>
            objective.execution_pieces?.map((piece) => ({
              id: piece.id,
              original_unit_price: piece.unit_price,
              original_warranty_in_days: piece.warranty_in_days,
              new_unit_price: undefined,
              new_warranty_in_days: undefined,
            })) || [],
        )

        const activities = auctionEntry?.forwarding?.directive_entry?.objectives?.flatMap(
          (objective) =>
            objective.execution_activities?.map((activity) => ({
              id: activity.id,
              original_unit_price: activity.unit_price,
              original_warranty_in_days: activity.warranty_in_days,
              new_unit_price: undefined,
              new_warranty_in_days: undefined,
            })) || [],
        )

        reset({
          start_at_date: undefined,
          start_at_time: undefined,
          deadline_to_complete_in_days: undefined,
          pieces,
          activities,
        });
      }, [auctionEntryIsPending]);

  useEffect(() => {
    setFormDisabled(createProposalIsPending);
  }, [createProposalIsPending]);

  return (
    <FormProvider {...formMethods}>
      <div className="inner-screen">
        <ShowHeader
          goBack
          goBackProps={{
            fallback: `/maintenance-establishment-choice/auction-entries/${auctionEntryId}`,
            searchLastRouteFromFallback: true,
          }}
          title={
            `Leilão ${auctionSummary?.directive_entry_type
              ? `(${maintenanceForwardingsDirectiveEntryKlasses[auctionSummary?.directive_entry_type]?.label})`
              : ''}`
          }
          subtitle={(
            <div className="flex flex-wrap gap-2 items-center">
              <p className="text-white-400">
                {auctionSummary?.equipment_model_name}
              </p>

              <ChevronsRightIcon className="text-white-400" />

              <p className="text-white-800">
                Nova proposta
              </p>
            </div>
          )}
          titleClassName="text-md"
          className="text-white-400"
          subtitleClassName="text-nowrap font-medium"
          buttons={(
            <div className="flex items-center gap-3">
              <Button
                variant="outline-error"
                onClick={() => goBack({
                  fallback: `/maintenance-establishment-choice/auction-entries/${auctionEntryId}`,
                  searchLastRouteFromFallback: true,
                })}
              >
                Cancelar
              </Button>

              <Button onClick={handleSubmit(onSubmit)} isLoading={createProposalIsPending}>
                Enviar lance para execução
              </Button>
            </div>
          )}
        />

        <ErrorMessageWrapper
          className="flex-1"
        // statusCode={!hasPermissionToUpdate ? 403 : forwardingError?.status}
        // refetchFunction={refetchForwarding}
        >
          <div className="flex-1 overflow-auto">
            <Box className="p-4 flex flex-col gap-4">
              <p className="text-lg font-medium">
                Expanda e edite os orçamentos por Objeto de manutenção
              </p>

              <div className="flex flex-col gap-2">
                <p className="text-sm font-medium">
                  Proposta original
                </p>

                <div className="flex gap-3 pb-3 border-b border-white-200">
                  <ResumeItem
                    className="text-sm"
                    label="Valor total"
                    value={
                      auctionEntry?.forwarding?.directive_entry?.total_value
                        ? formatCurrency(Number(auctionEntry?.forwarding?.directive_entry?.total_value))
                        :
                        '--'
                    }
                  />

                  <ResumeItem
                    className="text-sm"
                    label="Prazo em dias"
                    value={
                      auctionEntry?.forwarding?.directive_entry?.deadline_to_complete_in_days
                        ? `${auctionEntry.forwarding.directive_entry.deadline_to_complete_in_days} Dias`
                        :
                        '--'
                    }
                  />
                </div>
              </div>

              <div className="flex gap-3">
                <DateInput
                  fieldName="start_at_date"
                  control={control}
                  label="Data sugerida"
                  className="flex-1"
                  markAsRequired
                  errorMessage={errors.start_at_date?.message}
                />

                <Controller
                  control={control}
                  name="start_at_time"
                  render={({ field }) => (
                    <TextField
                      label="Horário sugerido"
                      type="time"
                      markAsRequired
                      className="flex-1"
                      LeftIcon={<ClockIcon className="w-5 h-5 text-white-400" />}
                      {...field}
                      value={field.value || ''}
                      errorMessage={errors.start_at_time?.message}
                    />
                  )}
                />

                <Controller
                  control={control}
                  name="deadline_to_complete_in_days"
                  render={({ field }) => (
                    <UnitTextField
                      decimals={0}
                      placeholder="Escreva aqui o prazo em dias"
                      unit="dia(s)"
                      label="Prazo para finalização da execução"
                      markAsRequired
                      className="flex-1"
                      {...field}
                      value={field.value || ''}
                      errorMessage={errors.deadline_to_complete_in_days?.message}
                    />
                  )}
                />
              </div>

              <ExecutionEntryUpdateObjectiveAttributes
                forwarding={auctionEntry?.forwarding}
                dataUpdatedAt={auctionEntryUpdatedAt}
              />
            </Box>
          </div>
        </ErrorMessageWrapper>
      </div>
    </FormProvider>
  );
}

export default CreateExecutionAuctionProposal;
